import React from "react"
import {
  Avatar,
  IconButton,
  Link as MuiLink,
  Menu,
  MenuItem,
  Theme,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core"
import { ArrowLeft, MoreVertical, Search } from "react-feather"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { paths } from "../../paths"
import { useUrlWithContext } from "../../hooks/use-url-with-context"

import { ChatResponse } from "../api/chat-response"
import ManageParticipantsDialog from "./ManageParticipantsDialog"
import DeleteChatDialog from "./DeleteChatDialog"
import RenameChatDialog from "./RenameChatDialog"
import { useMuteChatMutation } from "../api"
import { getChatIcon } from "../utils/chatUtil"

type Props = {
  title?: string
  subtitle?: string
  selectedChat?: ChatResponse
  orgId?: string
  // Rendered inside the project/task page itself, which already names and links the entity.
  embedded?: boolean
  // Returns to the chat list on small screens, where only one pane is shown at a time.
  onBack?: () => void
  searchOpen?: boolean
  onToggleSearch?: () => void
}

const ChatHeader = ({
  title = "",
  subtitle = "",
  selectedChat,
  orgId,
  embedded = false,
  onBack,
  searchOpen = false,
  onToggleSearch,
}: Props) => {
  const classes = useStyles()
  const { t } = useTranslation("chat")

  const [manageOpen, setManageOpen] = React.useState(false)
  const [deleteOpen, setDeleteOpen] = React.useState(false)
  const [renameOpen, setRenameOpen] = React.useState(false)

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)

  const [muteChat] = useMuteChatMutation()
  const { createPathWithContext } = useUrlWithContext()

  const openMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)

  const closeMenu = () => setAnchorEl(null)

  const entityPath = embedded
    ? undefined
    : selectedChat?.type === "project"
    ? createPathWithContext(paths.projectBasic(selectedChat.entityId))
    : selectedChat?.type === "task"
    ? createPathWithContext(paths.taskBasic(selectedChat.entityId))
    : undefined

  const isGroup = selectedChat?.type === "group"
  const isDm = selectedChat?.type === "dm"
  const ChatIcon = getChatIcon(selectedChat?.type ?? "")
  return (
    <div className={classes.root}>
      {onBack && (
        <IconButton className={classes.backButton} aria-label={t("chatHeader.backButtonLabel")} onClick={onBack}>
          <ArrowLeft size={20} />
        </IconButton>
      )}

      <Avatar className={classes.avatar}>
        <ChatIcon size={20} />
      </Avatar>

      <div className={classes.titles}>
        {embedded ? null : (
          <Typography variant="h6" noWrap>
            {title}
          </Typography>
        )}

        <Typography variant="body2" color="textSecondary" noWrap>
          {subtitle}

          {entityPath && (
            <>
              {" · "}

              <MuiLink component={Link} to={entityPath} color="inherit" className={classes.entityLink}>
                {selectedChat?.type === "project"
                  ? t("chatHeader.viewProjectLinkLabel")
                  : t("chatHeader.viewTaskLinkLabel")}
              </MuiLink>
            </>
          )}
        </Typography>
      </div>

      {selectedChat && (
        <>
          <Tooltip title={searchOpen ? t("closeSearchLabel") : t("searchMessagesLabel")}>
            <IconButton
              className={classes.menuButton}
              aria-label={searchOpen ? t("closeSearchLabel") : t("searchMessagesLabel")}
              onClick={onToggleSearch}
            >
              <Search size={18} />
            </IconButton>
          </Tooltip>

          <IconButton aria-label={t("chatHeader.optionsButtonLabel")} onClick={openMenu}>
            <MoreVertical size={18} />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            PaperProps={{
              style: {
                marginLeft: -20, // move menu 16px to the left
              },
            }}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={closeMenu}
          >
            <MenuItem
              onClick={() => {
                closeMenu()
                muteChat({ chatId: selectedChat.id, muted: !selectedChat.muted })
              }}
            >
              {selectedChat.muted ? t("chatHeader.unmuteLabel") : t("chatHeader.muteLabel")}
            </MenuItem>

            {isGroup && (
              <MenuItem
                onClick={() => {
                  closeMenu()
                  setManageOpen(true)
                }}
              >
                {t("chatHeader.manageParticipantsLabel")}
              </MenuItem>
            )}

            {!isDm && (
              <MenuItem
                onClick={() => {
                  closeMenu()
                  setRenameOpen(true)
                }}
              >
                {t("chatHeader.renameChatLabel")}
              </MenuItem>
            )}

            {(isDm || isGroup) && (
              <MenuItem
                className={classes.delete}
                onClick={() => {
                  closeMenu()
                  setDeleteOpen(true)
                }}
              >
                {t("chatHeader.deleteChatLabel")}
              </MenuItem>
            )}
          </Menu>
          <ManageParticipantsDialog
            open={manageOpen}
            chat={selectedChat}
            orgId={orgId!}
            onClose={() => setManageOpen(false)}
          />
          <RenameChatDialog open={renameOpen} chat={selectedChat} onClose={() => setRenameOpen(false)} />
          <DeleteChatDialog open={deleteOpen} chat={selectedChat} onClose={() => setDeleteOpen(false)} />
        </>
      )}
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,

    [theme.breakpoints.down("xs")]: {
      gap: theme.spacing(1),
      padding: theme.spacing(1),
    },
  },

  // Long chat names shrink and ellipsize instead of pushing the action buttons off screen.
  titles: {
    minWidth: 0,
  },

  backButton: {
    marginRight: -theme.spacing(1),
  },

  avatar: {
    width: 42,
    height: 42,
    flexShrink: 0,
  },

  entityLink: {
    textDecoration: "underline",
  },

  menuButton: {
    marginLeft: "auto",
  },

  delete: {
    color: theme.palette.error.main,
  },
  menu: {
    right: theme.spacing(4),
    marginRight: theme.spacing(2),
  },
}))

export default ChatHeader
